import {BrowserRenderMediaAudio as MediaAudio} from '@waka/compat/browser-render-media';
import {collectTtsAudioGroups, isTtsAudioItem} from '@waka/compat/tts-audio-groups';
import React, { createContext, useContext, useMemo } from 'react';
import {
  Html5Audio,
  useRemotionEnvironment,
  Sequence,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import { TrackType } from '../state/types';
import { useAllItems, useAssets } from '../utils/use-context';
import { useStablePreferredLocalUrl } from '../utils/find-asset-by-id';
import { RequireCachedAsset } from '../caching/require-cached-asset';
import { EditorStarterAsset } from '../assets/assets';
import { decibelToGain } from '../utils/decibels';
import {
  getPlaybackBoundaryWindowsForItems,
  isPlaybackBoundaryMediaPauseDisabledForWindows,
  type PlaybackBoundaryWindow,
} from './utils/playback-boundary-premount';

/**
 * 같은 TTS 오디오 파일을 사용하는 씬별 오디오 아이템들을 하나의 연속 오디오 엘리먼트로 재생.
 *
 * 문제: Remotion은 씬마다 별도 Html5Audio를 생성/파괴하여 전환 시 클릭/팝 아티팩트 발생.
 * 해결: 동일 assetId를 가진 TTS 오디오를 하나의 Sequence + Html5Audio로 통합하여
 *       씬 전환과 무관하게 오디오가 끊김 없이 연속 재생되도록 함.
 */

interface TtsAudioGroup {
  assetId: string;
  asset: EditorStarterAsset;
  fromFrame: number;
  toFrame: number;
  trimBeforeFrames: number;
  decibelAdjustment: number;
  playbackRate: number;
  trackMuted: boolean;
  boundaryWindows: PlaybackBoundaryWindow[];
}

// Preserve the vendor export for existing callers.
export {isTtsAudioItem};

/**
 * 글로벌 TTS 오디오를 위해 per-scene 렌더링에서 제외해야 하는 아이템 ID 세트 반환.
 * GlobalTtsAudioLayer와 동일한 그룹을 사용하여,
 * 실제로 글로벌 레이어에서 렌더링되는 아이템만 per-scene에서 제외함.
 */
export const useGlobalTtsItemIds = (tracks: TrackType[]): Set<string> => {
  const { items } = useAllItems();
  const { fps } = useVideoConfig();

  return useMemo(() => {
    const globalIds = new Set<string>();
    for (const group of collectTtsAudioGroups(items, tracks, fps)) {
      group.items.forEach((item) => globalIds.add(item.id));
    }

    return globalIds;
  }, [tracks, items, fps]);
};

/**
 * 단일 연속 TTS 오디오 렌더링
 */
const SingleTtsAudio: React.FC<{ group: TtsAudioGroup }> = ({ group }) => {
  const { fps } = useVideoConfig();
  const currentFrame = useCurrentFrame();
  const {isClientSideRendering}=useRemotionEnvironment();
  const asset = group.asset;
  const src = useStablePreferredLocalUrl(asset);

  const durationInFrames = group.toFrame - group.fromFrame;
  const premountFrames = Math.floor(3 * fps);
  // TTS groups can contain multiple scene items. Use per-item windows instead
  // of the group start frame so a later intro/main boundary still disables the
  // media pause at the real boundary.
  const disableBoundaryMediaPause =
    isPlaybackBoundaryMediaPauseDisabledForWindows({
      currentFrame,
      windows: group.boundaryWindows,
    });

  const volume = useMemo(() => {
    const gain = decibelToGain(group.decibelAdjustment);
    return () => gain;
  }, [group.decibelAdjustment]);

  return (
    <RequireCachedAsset asset={asset}>
      <Sequence
        from={group.fromFrame}
        durationInFrames={durationInFrames}
        premountFor={premountFrames}
        style={{ display: 'contents' }}
      >
        {isClientSideRendering ? <MediaAudio src={src} trimBefore={group.trimBeforeFrames} volume={volume} playbackRate={group.playbackRate} muted={group.trackMuted}/> : <Html5Audio
          crossOrigin="anonymous"
          pauseWhenBuffering={!disableBoundaryMediaPause}
          trimBefore={group.trimBeforeFrames}
          src={src}
          useWebAudioApi
          volume={volume}
          playbackRate={group.playbackRate}
          muted={group.trackMuted}
        />}
      </Sequence>
    </RequireCachedAsset>
  );
};

/**
 * 글로벌 TTS 오디오 레이어
 * tracks에서 TTS 오디오 아이템을 찾아 assetId별로 그룹핑 후 연속 재생
 */
export const GlobalTtsAudioLayer: React.FC<{
  tracks: TrackType[];
  boundaryPremountFramesByItemId?: ReadonlyMap<string, number>;
}> = ({ tracks, boundaryPremountFramesByItemId }) => {
  const { items } = useAllItems();
  const { assets } = useAssets();
  const { fps } = useVideoConfig();

  const ttsGroups = useMemo(() => {
    const groups: TtsAudioGroup[] = [];
    for (const {assetId, items: contiguous, trackMuted} of collectTtsAudioGroups(items, tracks, fps)) {
      const asset = assets[assetId];
      if (!asset) continue;

      const first = contiguous[0]!;
      const last = contiguous[contiguous.length - 1]!;
      groups.push({
        assetId,
        asset,
        fromFrame: first.from,
        toFrame: last.from + last.durationInFrames,
        trimBeforeFrames: first.audioStartFromInSeconds * fps,
        decibelAdjustment: first.decibelAdjustment,
        playbackRate: first.playbackRate,
        trackMuted,
        // Keep the exact item frames. Collapsing this to one max premount
        // would move later boundary overrides to the start of the TTS group.
        boundaryWindows: getPlaybackBoundaryWindowsForItems({
          items: contiguous,
          boundaryPremountFramesByItemId,
        }),
      });
    }

    return groups;
  }, [tracks, items, assets, fps, boundaryPremountFramesByItemId]);

  if (ttsGroups.length === 0) return null;

  return (
    <>
      {ttsGroups.map((group) => (
        <SingleTtsAudio
          key={`${group.assetId}-${group.fromFrame}`}
          group={group}
        />
      ))}
    </>
  );
};

/**
 * 글로벌 TTS로 처리되는 아이템 ID를 하위 컴포넌트에 전달하는 Context.
 * AudioLayer에서 이 set에 포함된 아이템은 per-scene 오디오 렌더링을 건너뜀.
 */
const GlobalTtsItemIdsContext = createContext<Set<string>>(new Set());

export const GlobalTtsItemIdsProvider: React.FC<{
  value: Set<string>;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <GlobalTtsItemIdsContext.Provider value={value}>
    {children}
  </GlobalTtsItemIdsContext.Provider>
);

export const useIsGlobalTtsItem = (itemId: string): boolean => {
  const globalIds = useContext(GlobalTtsItemIdsContext);
  return globalIds.has(itemId);
};
